import * as PIXI from 'pixi.js';
import { Container as PixiContainer, Rectangle } from 'pixi.js';
import isEqual from 'lodash/isEqual';
import type { OpaqueAsset } from '../../video-data/types';
import type { AspectRatio } from '../../aspectRatioConfig';
import type { MorphableProperty } from '../morph/MorphManager';
import { MorphManager } from '../morph/MorphManager';
import {
    assertArray,
    assertAssetName,
    assertBoolean,
    assertInArray,
    assertInEnum,
    assertNumberBetween,
    assertObject,
} from '../../validators';
import type { AddAssetSfxSettings, AssetSfxItem, SfxOwner } from '../../audio/sfx-types';
import { AssetSfxEvent } from '../../audio/sfx-types';
import { AudioAsset } from '../../audio/AudioAsset';
import uuid from '../../video-data/utils/uuid';
import { getPositionFromTransformation } from '../../video-data/designs/scenes';
import { CircleMask } from '../mask/CircleMask';
import { PolygonMask } from '../mask/PolygonMask';
import { Config } from '../../video-data/config';

import {
    AdjustmentFilter,
    BlurFilter,
    ColorChannelMapFilter,
    DuoToneFilter,
    GreyscaleFilter,
    HslAdjustmentFilter,
    OldFilmFilter,
} from '../filters';
import type { Filter } from '../filters/Filter';
import type {
    AssetInterface,
    AssetTemplateReferenceMap,
    DestroyOptions,
    DrawableLifecycleAware,
    FetchResources,
    Interpolation,
    Resolution,
    Transformation as TransformationType,
} from '../../types';

import { Transformation } from '../Transformation';
import { BooleanAttribute, ColorAttribute, getAllTemplateAttributes } from '../attributes';
import {
    getTemplateValueAttribute,
    objectHasTemplateValues,
    stripTemplateReferencePrefix,
} from '../attributes/template/common';
import { AssetAnchorDisplay, AssetBoundsDisplay, AssetMaxBoundsDisplay } from '../helper';
import { rgb2int } from '../../libs/videoData';
import type { BufferingMode, SeekMode } from '../../enums';
import { AssetCreatedFrom, ValueMapMode } from '../../enums';
import type {
    LoadSettings,
    SerializableMixinInterface,
    SerializableMixinSettings,
} from '../serializableMixin';
import { TypedSerializableMixin } from '../serializableMixin';
import { LuminaryBase } from '../LuminaryBase';
import type { SceneGroup } from '../SceneGroup';
import type { Template } from '../template/Template';
import type { Scene } from '../scene/Scene';
import type { Subscene } from '../scene/Subscene';
import { StencilMask } from '../mask/StencilMask';
import { ColorGradingFilter } from '../filters/ColorGradingFilter';
import type { DynamicAnimationConfiguration } from '../../libs/DynamicAnimationConfiguration';
import type { RemoveAnchoredPoints } from '../../libs/Curve';
import type { BaseTimeline } from '../../libs/BaseTimeline';
import type { TickerTimeline } from '../../libs/TickerTimeline';
import { MorphEngine } from '../morph/MorphEngine';
import { ErrorReporter } from '../../libs/error-reporter';
import type { SerializedAnimationConfig } from '../../libs/AnimationCurveManager';
import { AnimationCurveManager } from '../../libs/AnimationCurveManager';
import type { CanvasReusableTexturesManager } from '../../canvasReusableTexuresManager';
import type { AssetNetworkManager } from '../AssetNetworkManager';
import type { DrivenObject } from '../../libs/AnimationCurve';
import { cloneDeep } from '../../libs/cloneDeep';
import type Luminary from '../../';
import type { CompositingAsset } from '../CompositingAsset';
import type { FancyCaptionAsset } from './FancyCaptionAsset/asset/FancyCaptionAsset';
import type { TextAsset } from './TextAsset/TextAsset';
import type { CaptionAsset } from './CaptionAsset/asset/CaptionAsset';
import type { ColorAsset } from './ColorAsset';
import type { LottieAsset } from './LottieAsset/LottieAsset';
import type { AssetGroup } from './AssetGroup';
import type { IconAsset } from './IconAsset';
import type { CircleAsset } from './CircleAsset';
import type { GradientAsset } from './GradientAsset';
import type { ChartAsset } from './ChartAsset';
import type { HtmlAsset } from './HtmlAsset';
import type { RectangleAsset } from './RectangleAsset';
import type { TimeAwareMediaAsset } from './TimeAwareMediaAsset';
import { findTemplateIndexErrors, getStylePropertiesToCopy, uniqueName } from './utils';

export type GetAssetsOptions = {
    /**
     * do we just want to look at all children or do we want to search through all or descendants
     */
    deep: boolean;
};

type AssetTypeConstant =
    | typeof Config.TEXT_ASSET_TYPE
    | typeof Config.FANCY_CAPTION_ASSET_TYPE
    | typeof Config.CAPTION_ASSET_TYPE
    | typeof Config.MEDIA_ASSET_TYPE
    | typeof Config.GROUP_ASSET_TYPE
    | typeof Config.LOTTIE_ASSET_TYPE
    | typeof Config.ICON_ASSET_TYPE
    | typeof Config.COLOR_ASSET_TYPE
    | typeof Config.CIRCLE_ASSET_TYPE
    | typeof Config.GRADIENT_ASSET_TYPE
    | typeof Config.RECTANGLE_ASSET_TYPE
    | typeof Config.COMPOSITING_ASSET_TYPE
    | typeof Config.CHART_ASSET_TYPE
    | typeof Config.HTML_ASSET_TYPE;

type AssetTypeClassMap = {
    [Config.TEXT_ASSET_TYPE]: TextAsset;
    [Config.FANCY_CAPTION_ASSET_TYPE]: FancyCaptionAsset;
    [Config.CAPTION_ASSET_TYPE]: CaptionAsset;
    [Config.MEDIA_ASSET_TYPE]: TimeAwareMediaAsset;
    [Config.GROUP_ASSET_TYPE]: AssetGroup;
    [Config.LOTTIE_ASSET_TYPE]: LottieAsset;
    [Config.ICON_ASSET_TYPE]: IconAsset;
    [Config.COLOR_ASSET_TYPE]: ColorAsset;
    [Config.CIRCLE_ASSET_TYPE]: CircleAsset;
    [Config.GRADIENT_ASSET_TYPE]: GradientAsset;
    [Config.RECTANGLE_ASSET_TYPE]: RectangleAsset;
    [Config.COMPOSITING_ASSET_TYPE]: CompositingAsset;
    [Config.CHART_ASSET_TYPE]: ChartAsset;
    [Config.HTML_ASSET_TYPE]: HtmlAsset;
};

type AssetTypes = { [K in AssetTypeConstant]: AssetTypeClassMap[K] };

export type GetAssetType<T> = T extends keyof AssetTypes ? AssetTypes[T] : AssetTypes[AssetType];

type AssetTypesWithMixedTextTypes<T extends AssetType> = T extends typeof Config.TEXT_ASSET_TYPE
    ? TextAsset
    : GetAssetType<T>;

export type GetAssetTypeWithMixedTextTypes<T> = T extends keyof AssetTypes
    ? AssetTypesWithMixedTextTypes<T>
    : AssetTypes[AssetType];

interface AssetParentTypes {
    [Config.GROUP_ASSET_TYPE]: AssetGroup;
}

type CompositingAssetName = `compositing_${number}`;
type TextAssetName = `text_${number}`;
type MediaAssetName = `media_${number}`;
type GroupAssetName = `group_${number}`;
type LottieAssetName = `lottie_${number}`;
type IconAssetName = `icon_${number}`;
type ColorAssetName = `color_${number}`;
type CircleAssetName = `circle_${number}`;
type GradientAssetName = `gradient_${number}`;
type RectangleAssetName = `rectangle_${number}`;
type WatermarkAssetName = `watermark_${number}`;
type AttributionAssetName = `attribution_${number}`;
type TextGroupAssetName = `text_group_${number}`;
type ChartAssetName = `chart_${number}`;

export type AssetName =
    | CompositingAssetName
    | TextAssetName
    | MediaAssetName
    | GroupAssetName
    | LottieAssetName
    | IconAssetName
    | ColorAssetName
    | CircleAssetName
    | GradientAssetName
    | RectangleAssetName
    | WatermarkAssetName
    | AttributionAssetName
    | TextGroupAssetName
    | ChartAssetName
    | string;

export type GetAssetTypeByName<T> = T extends TextAssetName
    ? TextAsset
    : T extends AttributionAssetName
      ? TextAsset
      : T extends MediaAssetName
        ? TimeAwareMediaAsset
        : T extends GroupAssetName
          ? AssetGroup
          : T extends TextGroupAssetName
            ? AssetGroup
            : T extends LottieAssetName
              ? LottieAsset
              : T extends IconAssetName
                ? IconAsset
                : T extends ColorAssetName
                  ? ColorAsset
                  : T extends CircleAssetName
                    ? CircleAsset
                    : T extends GradientAssetName
                      ? GradientAsset
                      : T extends RectangleAssetName
                        ? RectangleAsset
                        : T extends WatermarkAssetName
                          ? TimeAwareMediaAsset
                          : T extends CompositingAssetName
                            ? CompositingAsset
                            : T extends ChartAssetName
                              ? ChartAsset
                              : Asset;

export type Asset = AssetTypes[keyof AssetTypes];

export type AssetType = keyof AssetTypes;
export type AssetParentType = keyof AssetParentTypes;

// TODO(strict-ts): AssetContainer union type (BaseGroup | SceneGroup | CaptionsManager) doesn't work
// because these types don't share common properties like `duration` and `isPlaying` that code expects.
// A proper fix would require either:
// 1. Creating a common interface that all containers must implement
// 2. Adding missing properties to CaptionsManager
// 3. Type narrowing at each usage site
// For now, container remains `any` in asset constructors.

export type ContainerSettings = SerializableMixinSettings & {
    container: unknown;
};

type ResolutionProperty = { width?: number; height?: number };

type GetMaskType<T extends MaskType> = T extends typeof Config.CIRCLE_MASK_TYPE
    ? CircleMask
    : T extends typeof Config.POLYGON_MASK_TYPE
      ? PolygonMask
      : never;
type MaskType = typeof Config.CIRCLE_MASK_TYPE | typeof Config.POLYGON_MASK_TYPE;

export type MorphParameters = {
    duration: number;
    interpolation: Interpolation;
    properties?: string[];
    alphaStep?: boolean;
    alphaOnly?: boolean;
};

enum AssetMode {
    /**
     * Set initial values
     */
    BUILD = 'BUILD',
    /**
     * Evaluate values
     */
    EVAL = 'EVAL',
}

const ASSET_NAME_REGEX = /(.*)_(\d+)(_.*)?/;

export type BaseAssetSettings = {
    /**
     * if updates should bubble upward
     * @default false
     */
    isFinalTopLevel?: boolean;
    /**
     * if drawing should happen automatically
     * @default false
     */
    autoDraw?: boolean;
    /**
     * triggers a redraw if true
     * @default true
     */
    requiresRedraw?: boolean;
    /**
     * redraw events should bubble upward
     * @default false
     */
    updateParentOnRedraw?: boolean;
    /**
     * redraw events should bubble upward (through luminaryParent)
     * @default false
     */
    updateLuminaryParentOnRedraw?: boolean;
    /**
     * object on which to emit update events
     * @default this
     */
    updateEventSource?: UpdateEventSource;
    /**
     * prevents assets from loading if false. A boolean will set all options to that value
     */
    fetchResources: FetchResources;
    /**
     * enables the boundsWithoutAnimation api
     * It's an expensive api to support for large & deep trees (like captions right now),
     * so we're making it opt-in for the use cases we need for now.
     * @default false
     */
    enableBoundsWithoutAnimationForSubtree?: boolean;
    /**
     * how the asset was created
     * @default AssetCreatedFrom.SCENE_DESIGN
     */
    createdFrom?: AssetCreatedFrom;
    assetNetworkManager: AssetNetworkManager;
    /**
     * returns the current aspect ratio of the video, which can change at runtime
     */
    getAspectRatio: () => AspectRatio;
    reusableTextures: CanvasReusableTexturesManager;
    /**
     * the global timeline this asset is positioned relative to
     */
    timeline: TickerTimeline;
    getReferenceResolution: () => Resolution;
    getOutputResolution: () => Resolution;
} & Omit<ContainerSettings, 'container'>;

type UpdateEventSource = PIXI.utils.EventEmitter & {
    get name(): string;
};

// TODO(strict-ts): Change to Record<string, unknown> when dependent files are updated
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CopyPasteData = Record<string, any> & {
    _templateReferenceMap: Record<string, string>;
};

export interface CopyPasteStyle extends Pick<SerializableMixinInterface, 'save'> {
    get stylePropertiesToCopy(): string[];
    // TODO(strict-ts): Change to CopyPasteData when dependent files are updated
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    copyStyleProperties(): Record<string, any>;
    pasteStyleProperties(settings: CopyPasteData): void;
    buildTemplateReferenceMap(): AssetTemplateReferenceMap;
}

/**
 * This BaseAsset class is the base class for MediaAsset and TextAsset, ensuring a consistent API.  This is an abstract
 * base class so methods will throw exceptions if not overwritten.
 */
export class BaseAsset
    extends TypedSerializableMixin(LuminaryBase)
    implements AssetInterface, CopyPasteStyle, DrawableLifecycleAware, SfxOwner
{
    override _classType = 'BaseAsset';

    /**
     * Internal PIXI.Container instance that handles all PIXI display object operations.
     */
    #pixiContainerInstance: PIXI.Container;

    /**
     * Luminary-level parent reference for the hierarchy.
     */
    #luminaryParent: BaseAsset | null = null;

    /**
     * Luminary-level children array for the hierarchy.
     */
    #luminaryChildren: BaseAsset[] = [];

    /**
     * The container which controls the behaviour of this object
     * @deprecated - I think we can just use parent instead of container. I'll revisit this later, but for now
     * try to avoid using this property.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    container: any = undefined;

    #root: Luminary | null = null;

    /**
     * The luminary instance this object belongs to. Throws if accessed after the
     * asset has been detached/destroyed.
     */
    get root(): Luminary {
        if (this.#root === null) {
            throw new Error('Asset root accessed before attach or after destroy');
        }
        return this.#root;
    }

    /**
     * The root, or null if the asset is not currently attached. For code paths
     * (e.g. teardown) that must tolerate a detached asset.
     * @internal
     */
    protected get _rootOrNull(): Luminary | null {
        return this.#root;
    }

    /**
     * The global timeline this asset is positioned relative to. Injected at construction.
     */
    #timeline: TickerTimeline;

    /**
     * Texture pool. Cached at construction time since the instance never changes.
     * @internal
     */
    readonly _reusableTextures: CanvasReusableTexturesManager;

    /**
     * Network slot manager. Cached at construction time since the instance never changes.
     * @internal
     */
    readonly _assetNetworkManager: AssetNetworkManager;

    #slide: Scene | null = null;

    /**
     * The scene instance this object belongs to. Throws if accessed after the
     * asset has been detached/destroyed.
     */
    get slide(): Scene | null {
        return this.#slide;
    }

    /**
     * The slide, for internal code paths that only run while the asset is attached
     * to one. Throws if the asset has no slide.
     * @internal
     */
    protected get _requireSlide(): Scene {
        if (this.#slide === null) {
            throw new Error('Asset is not attached to a slide');
        }
        return this.#slide;
    }

    #template: Template | null = null;

    /**
     * The template instance this object uses. Throws if accessed before the asset
     * has been attached to a root.
     */
    get template(): Template {
        if (this.#template === null) {
            throw new Error('Asset template accessed before attach');
        }
        return this.#template;
    }

    /**
     * The template, or null if the asset is not attached. For code paths that
     * must tolerate a detached asset.
     * @internal
     */
    protected get _templateOrNull(): Template | null {
        return this.#template;
    }

    /**
     * Animation configuration store. This variable holds a list of animation curve descriptions which are being applied
     * during `_applyAnimation`.
     */
    #animationConfig: SerializedAnimationConfig[] = [];

    #animationManager: AnimationCurveManager;

    protected getReferenceResolution: () => Resolution;

    protected getOutputResolution: () => Resolution;

    #processing = false;

    /** @internal */
    _isFinalTopLevel = false;

    // evaluation mode - are we building or playing back?
    #drawEvaluationMode: AssetMode = AssetMode.BUILD;

    // determines whether or not we immediately want to process changes
    #autoDraw = false;

    /** @internal */
    protected _getAspectRatio: () => AspectRatio;

    // determines whether or not the object has changed and needs to be updated
    #requiresRedraw = true;

    /** @internal */
    _updateParentOnRedraw = false;

    /** @internal */
    _updateLuminaryParentOnRedraw = false;

    /** @internal */
    _updateEventSource: UpdateEventSource = this;

    @BooleanAttribute({ serialize: false })
    accessor displayBounds = false;
    @ColorAttribute({ serialize: false })
    accessor boundsColor = rgb2int([6, 180, 0]);
    @BooleanAttribute({ serialize: false })
    accessor displayMaxBounds = false;
    @ColorAttribute({ serialize: false })
    accessor maxBoundsColor = rgb2int([246, 70, 70]);
    @BooleanAttribute({ serialize: false })
    accessor displayAnchor = false;
    @BooleanAttribute({ serialize: false })
    accessor displayMaskHelpers = false;
    @ColorAttribute({ serialize: false })
    accessor anchorColor = rgb2int([88, 70, 246]);

    // allows assets from a remote source to be loaded
    readonly #fetchResources: FetchResources;

    protected sentCanPlay = false;

    /**
     * Determines whether or not this object is controlling the position of its child(ren). If it is then we can skip
     * certain steps such as setPosition.
     * @internal
     */
    _controlsChildPosition = false;

    /**
     * Specifies if the asset has any user editable properties
     */
    #isEditable = true;

    /**
     * Specifies if the asset has any user editable colors
     */
    #isColorEditable = false;

    /**
     * The transformation is in control of serializing and deserializing the transformation of a PIXI container and
     * propagates these changes to the container through `applyTransformation`.
     */
    transformation: Transformation = new Transformation({
        parent: this,
    });

    /** @internal */
    _childrenRequestingRedraw: BaseAsset[] = [];

    // allows us to define when the intro animation for this asset begins.
    #introAnimationOffset = 0;

    // allows us to define when the outro animation for this asset begins.
    #outroAnimationOffset = 0;

    #boundsWithoutAnimation: Rectangle = Rectangle.EMPTY;

    #enableBoundsWithoutAnimationForSubtree = false;

    /**
     * The filters that are applied to this asset. These are the luminary filters, not the pixi filters.
     * The pixi filters applied to the container are in the pixi `filters` property.
     */
    #filters: Filter[] = [];

    /**
     * These animation behaviours are only available for assets which offer animation.
     * Currently, it's assets that extend DynamicStylingMixin and TextAssets
     */
    #animateOutBehaviour = Config.ANIMATE_OUT_BEHAVIOUR_NEVER;
    #animationAnchorsToRemove: RemoveAnchoredPoints = Config.END_ANIMATION_CURVE_ANCHOR;

    /** @internal */
    _morphParameters: MorphParameters = {
        duration: Config.DEFAULT_MORPH_DURATION,
        interpolation: Config.LINEAR_INTERPOLATION_PRESET,
    };

    get morphInterpolation(): Interpolation {
        return this._morphParameters.interpolation;
    }

    set morphInterpolation(value: Interpolation) {
        this._morphParameters = { ...this._morphParameters, interpolation: value };
    }

    get morphAlphaStep(): boolean {
        return this._morphParameters.alphaStep ?? false;
    }

    set morphAlphaStep(value: boolean) {
        this._morphParameters = { ...this._morphParameters, alphaStep: value };
    }

    get morphAlphaOnly(): boolean {
        return this._morphParameters.alphaOnly ?? false;
    }

    set morphAlphaOnly(value: boolean) {
        this._morphParameters = { ...this._morphParameters, alphaOnly: value };
    }

    @BooleanAttribute({ serialize: false })
    accessor isMatchCutDuplicate = false;

    #morphEngine: MorphEngine = new MorphEngine(this);

    /**
     * Sound effects attached to this asset. Each SFX is triggered by a specific event.
     */
    #sfx: AssetSfxItem[] = [];

    /**
     * Audio assets for SFX playback. Each SFX item has a corresponding AudioAsset
     * that handles the actual audio playback.
     */
    #sfxAudioAssets: Map<string, AudioAsset> = new Map();

    /**
     * @param container - the container used to parent the asset
     * @param settings - configuration settings
     */
    // TODO(strict-ts): container type should be a union of valid parent types (BaseGroup, CaptionsManager, etc.)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(container: any, settings: BaseAssetSettings) {
        if (settings.name) {
            assertAssetName(settings.name);
        } else {
            settings = { ...settings, name: BaseAsset.name };
        }
        super(settings);

        this.#pixiContainerInstance = new PIXI.Container();
        this.#pixiContainerInstance.name = settings.name || '';

        if (container === undefined) {
            throw Error(`Container is not defined. Unable to access root.`);
        }
        this.container = container;

        assertObject(container.root);
        this.#root = this.container.root;
        this.#timeline = settings.timeline;
        this._reusableTextures = settings.reusableTextures;
        this._assetNetworkManager = settings.assetNetworkManager;

        assertObject(container.slide);
        this.#slide = this.container.slide;

        this.#template = this.root.template;

        this._getAspectRatio = settings.getAspectRatio;

        this.#animationManager = new AnimationCurveManager(container);
        this.#animationManager.name = this.name + '_animationManager';

        // Since we don't want to serialize these properties we can't use our load method and thus have to
        // load them by hand. Once we have Attributes we could define these as writable = false.
        if (settings.hasOwnProperty('isFinalTopLevel')) {
            this._isFinalTopLevel = settings.isFinalTopLevel!;
        }
        if (settings.hasOwnProperty('autoDraw')) {
            this.#autoDraw = settings.autoDraw!;
        }
        if (settings.hasOwnProperty('requiresRedraw')) {
            this.#requiresRedraw = settings.requiresRedraw!;
        }
        if (settings.hasOwnProperty('updateParentOnRedraw')) {
            this._updateParentOnRedraw = settings.updateParentOnRedraw!;
        }
        if (settings.hasOwnProperty('updateLuminaryParentOnRedraw')) {
            if (this._updateParentOnRedraw) {
                // Using both systems would be a performance anti-pattern, because it would repeat many updates.
                throw new Error(
                    'This system only supports one of updateLuminaryParentOnRedraw or updateParentOnRedraw',
                );
            }
            this._updateLuminaryParentOnRedraw = settings.updateLuminaryParentOnRedraw!;
        }
        if (settings.hasOwnProperty('updateEventSource')) {
            if (typeof settings.updateEventSource!.emit !== 'function') {
                throw Error(`Invalid event emitter ${settings.updateEventSource}`);
            }
            this._updateEventSource = settings.updateEventSource!;
        }
        if (settings.hasOwnProperty('enableBoundsWithoutAnimationForSubtree')) {
            this.#enableBoundsWithoutAnimationForSubtree =
                settings.enableBoundsWithoutAnimationForSubtree!;
        }

        this.#fetchResources = settings.fetchResources;

        this.getReferenceResolution = settings.getReferenceResolution;
        this.getOutputResolution = settings.getOutputResolution;
    }

    /**
     * Luminary-level parent reference.
     */
    get parent(): BaseAsset | null {
        return this.#luminaryParent;
    }

    set parent(value: BaseAsset | null) {
        this.#luminaryParent = value;
    }

    /**
     * Luminary-level children array.
     */
    get children(): readonly BaseAsset[] {
        return this.#luminaryChildren;
    }

    /**
     * Returns the subscene if there is one, or null, if the asset is at the scene level.
     */
    get subscene(): Subscene | null {
        return this.getFirstParentOfType('Subscene') as Subscene;
    }

    /**
     * Return the parentId
     */
    get parentId(): string | null {
        // if the parent doesn't exist, return null
        if (!this.parent) {
            return null;
        }
        const assetParent = this.getParent();
        if (!assetParent) {
            return null;
        }
        // if we're parented to a subslide, return null
        if (assetParent._classType === 'Subscene') {
            return null;
        }
        // if the CaptionsManager is the parent, return null
        if (assetParent._classType === 'CaptionsManager') {
            return null;
        }
        // if we're parented to the top level (ie. Scene's workScene), return null
        if (assetParent._classType === 'BaseGroup') {
            return null;
        }

        return assetParent.id;
    }

    /**
     * Get all of the assets
     */
    getAssets(options: GetAssetsOptions = { deep: true }): Asset[] {
        return this.#recursiveGetChildren([], options).filter(Config.isAsset);
    }

    #recursiveGetChildren(children: BaseAsset[], options: GetAssetsOptions): BaseAsset[] {
        this.children.forEach((child) => {
            if (typeof child.#recursiveGetChildren !== 'function') {
                return;
            }
            if (Config.isAsset(child)) {
                children.push(child);
            }

            if (options.deep) {
                child.#recursiveGetChildren(children, options);
            }
        });
        return children;
    }

    /**
     * Get the first parent of a given class type
     * @param type - the class type of the parent we're looking for
     */
    getFirstParentOfType(type: string): BaseAsset | null {
        let parent = this.parent;
        while (parent) {
            if (parent.classType === type) {
                return parent;
            }
            parent = parent.parent;
        }
        return parent;
    }

    /**
     * Steps up through the hierarchy to find the first parent that is either an Asset, the Scene container
     * or the Subscene container.
     * Should be used outside of luminary sparingly, as it can return some internal containers, which
     * we normally wouldn't interact with externally.
     */
    getParent(): BaseAsset | null {
        let parent = this.parent;
        while (parent) {
            if (
                Config.isAsset(parent) ||
                parent === this.#slide?.scene ||
                parent === this.#slide?.stickers ||
                parent === this.subscene
            ) {
                return parent;
            }
            parent = parent.parent;
        }
        return null;
    }

    /**
     * Determine whether or not we have the object of the given name in our hierarchy
     * @param name - the name of the object we're searching for
     */
    hasAssetByName(name: string, options: GetAssetsOptions = { deep: true }): boolean {
        return this.getAssets(options).filter((item) => item.name === name).length > 0;
    }

    /**
     * Get all assets by name by traversing our hierarchy.
     * @param name - the name of the object we're searching for
     */
    getAssetByName<T extends AssetName>(
        name: T,
        options?: GetAssetsOptions,
    ): GetAssetTypeByName<T> | undefined;
    getAssetByName(name: string, options: GetAssetsOptions = { deep: true }): Asset | undefined {
        return this.getAssets(options).find((item) => item.name === name);
    }

    /**
     * Get the asset with the given UUID in our hierarchy
     */
    getAssetById(id: string, options: GetAssetsOptions = { deep: true }): Asset | undefined {
        for (const child of this.getAssets(options)) {
            if (child.id === id) {
                return child;
            }
        }
    }

    /**
     * Remove and destroy all children
     * @param childrenToSkip - a list of children we don't want to remove
     */
    clear(childrenToSkip: BaseAsset[] = []) {
        const skipSet = new Set(childrenToSkip);
        const childrenToDestroy = [...this.children].filter((child) => !skipSet.has(child));
        childrenToDestroy.forEach((child) => child.destroy(true));
    }

    /**
     * Get the classTypes of all of our descendants
     */
    getAssetTypes(options?: GetAssetsOptions): AssetType[] {
        return [...new Set(this.getAssets(options).map((item) => item.classType as AssetType))];
    }

    /**
     * Get all assets of the given classType in our hierarchy.
     * @param classType - the classType of the asset we're searching for
     */
    getAssetsByType<T extends AssetType>(
        classType: T,
        options?: GetAssetsOptions,
    ): GetAssetTypeWithMixedTextTypes<T>[];
    getAssetsByType(classType: AssetType, options: GetAssetsOptions = { deep: true }): Asset[] {
        return this.getAssets(options).filter((item) => item.classType === classType);
    }

    /**
     * Returns the first parent of the given type. If the parent is not found, returns undefined.
     */
    getParentByType<T extends AssetParentType>(classType: T): GetAssetType<T>;
    getParentByType(classType: AssetParentType): Asset | undefined {
        let parent = this.parent;
        while (parent) {
            if (Config.isAsset(parent) && parent.classType === classType) {
                return parent;
            }
            parent = parent.parent;
        }
        return undefined;
    }

    set animationManager(value: AnimationCurveManager) {
        this.#animationManager = value;
    }

    /**
     * The object's AnimationCurveManager which manages the creation and evaluation of animation curves.
     */
    get animationManager(): AnimationCurveManager {
        return this.#animationManager;
    }

    set animationConfig(data) {
        assertArray(data);
        if (isEqual(this.#animationConfig, data)) {
            return;
        }
        this.#animationConfig = data;
        this.requiresRedraw = true;
    }

    /**
     * @deprecated We use dynamic animation now, but we still have some old scene designs which use raw
     * animation configuration data.
     */
    get animationConfig() {
        return this.#animationConfig;
    }

    /**
     * Removes all animation by deleting all animation curves created by the `AnimationCurveManager`.
     */
    clearAnimation() {
        if (!this.animationManager) {
            return;
        }
        this.animationManager.clear();
    }

    /**
     * Evaluates all animation curves at the given progress value.
     * @param percentage - A value between 0 and 1
     * @internal
     */
    _evalAnimationAtPercentage(percentage: number) {
        this.animationManager.percentage = percentage;
    }

    /**
     * Applies the animation configuration specified in `animationConfig`.
     * @deprecated We use dynamic animation now, but we still have some old scene designs which use this
     * @internal
     */
    _applyAnimation() {
        if (!this.animationManager) {
            return;
        }
        if (!this.animationConfig) {
            return;
        }
        // create the individual animation curves
        this.animationConfig.forEach((config) => {
            const isIntroAnimation =
                config.curve?.points?.some(
                    (p) => p.anchor === Config.START_ANIMATION_CURVE_ANCHOR,
                ) ?? false;
            if (!this.hasMorphLeadInAnimation || !isIntroAnimation) {
                this.#resolveDescriptionAndCreateCurve(config);
                return;
            }

            const shouldSeekMaskToEndState =
                this.mask && config.drivenProperty.includes('mask.transition');
            if (!shouldSeekMaskToEndState) {
                return;
            }
            const range = config.drivenRange as [number, number];
            (this.mask as StencilMask).transition = range[1];
        });
    }

    /**
     * Create an individual animation curve which drives one of more properties. This happens in 2 stages:
     * - resolve any property references and update curve description
     * - create the animation curve
     */
    #resolveDescriptionAndCreateCurve(config: SerializedAnimationConfig) {
        if (typeof config !== 'object') {
            return;
        }
        // clone the object so we don't loose our original references
        const localConfig = cloneDeep(config);

        // Some curve descriptions require some of their range values to be calculated based on the current setup which
        // is what we resolve here.
        // Note: drivenRange can contain string or object values that get resolved to numbers at runtime,
        // but SerializedAnimationConfig types it as [number, number]. Cast as needed.
        const drivenRange = localConfig.drivenRange as unknown as [
            number | string | { width?: number; height?: number },
            number | string | { width?: number; height?: number },
        ];
        if (typeof drivenRange[0] === 'string') {
            drivenRange[0] = this.#resolvePropertyReference(drivenRange[0]);
        } else if (typeof drivenRange[0] === 'object') {
            drivenRange[0] = this.#resolveObjectProperty(drivenRange[0]);
        }
        if (typeof drivenRange[1] === 'string') {
            drivenRange[1] = this.#resolvePropertyReference(drivenRange[1]);
        } else if (typeof drivenRange[1] === 'object') {
            drivenRange[1] = this.#resolveObjectProperty(drivenRange[1]);
        }

        // create the actual animation
        // After resolution, drivenRange contains only numbers
        this.animationManager.add(localConfig, {
            drivenObjects: [this as unknown as DrivenObject],
            duration: this._requireSlide.duration,
            drivenRange: drivenRange as [number, number],
        });
    }

    #resolveObjectProperty(objectProperty: ResolutionProperty): number {
        // validate data
        if (Object.keys(objectProperty).length > 1) {
            throw Error('Invalid range specified.  Should be {"property":value}');
        }
        const key = Object.keys(objectProperty)[0] as keyof ResolutionProperty;
        let result = 0;
        switch (key) {
            case 'width':
                result = this.getReferenceResolution().width * objectProperty['width']!;
                break;
            case 'height':
                result = this.getReferenceResolution().height * objectProperty['height']!;
                break;
            default:
                key satisfies never;
                ErrorReporter.setContext('property', key);
                throw new Error('Could not handle property');
        }
        return result;
    }

    #resolvePropertyReference(propertyReference: string): number {
        // extract multiplier
        let multiplier = 1;
        if (propertyReference.startsWith('-')) {
            multiplier = -1;
            propertyReference = propertyReference.slice(1, propertyReference.length);
        }

        // resolve reference
        let result = 0;
        switch (propertyReference) {
            // WIDTH
            case 'slide_width':
                result = this.getReferenceResolution().width;
                break;
            case 'object_width': // TODO: not in use, can we remove?
                result = this._pixiContainer.width;
                break;
            case 'mask_width':
                if (!(this._pixiContainer.mask instanceof PIXI.Container)) {
                    throw Error(
                        `Object ${this.name} does't have a mask. Unable to retrieve height (${this._pixiContainer.mask}).`,
                    );
                }
                result = this._pixiContainer.mask.width;
                break;
            case 'object_width_minus_mask_width': // TODO: not in use, can we remove?
                if (!(this._pixiContainer.mask instanceof PIXI.Container)) {
                    throw Error(
                        `Object ${this.name} does't have a mask. Unable to retrieve height (${this._pixiContainer.mask}).`,
                    );
                }
                result = this._pixiContainer.width - this._pixiContainer.mask.width;
                break;
            case 'slide_width_minus_mask_width': // TODO: not in use, can we remove?
                if (!(this._pixiContainer.mask instanceof PIXI.Container)) {
                    throw Error(
                        `Object ${this.name} does't have a mask. Unable to retrieve height (${this._pixiContainer.mask}).`,
                    );
                }
                result = this.getReferenceResolution().width - this._pixiContainer.mask.width;
                break;
            // HEIGHT
            case 'slide_height':
                result = this.getReferenceResolution().height;
                break;
            case 'object_height': // TODO: not in use, can we remove?
                result = this._pixiContainer.height;
                break;
            case 'mask_height':
                if (!(this._pixiContainer.mask instanceof PIXI.Container)) {
                    throw Error(
                        `Object ${this.name} does't have a mask. Unable to retrieve height.`,
                    );
                }
                result = this._pixiContainer.mask.height;
                break;
            case 'slide_height_minus_mask_height': // TODO: not in use, can we remove?
                if (!(this._pixiContainer.mask instanceof PIXI.Container)) {
                    throw Error(
                        `Object ${this.name} does't have a mask. Unable to retrieve height.`,
                    );
                }
                result = this.getReferenceResolution().height - this._pixiContainer.mask.height;
                break;
        }

        if (Number.isNaN(result)) {
            throw Error(`PropertyReference for ${propertyReference} is NaN`);
        }

        return result * multiplier;
    }

    /**
     * @internal
     */
    get _pixiContainer(): PIXI.Container {
        return this.#pixiContainerInstance;
    }

    /**
     * Adds a child to the internal PIXI.Container and updates Luminary parent/children references.
     * @internal
     */
    _addPixiChild(child: PIXI.DisplayObject) {
        return this.#pixiContainerInstance.addChild(child);
    }

    /**
     * Adds a child to the internal PIXI.Container at a specific index and updates Luminary references.
     * @internal
     */
    _addPixiChildAt(child: PIXI.DisplayObject, index: number) {
        return this.#pixiContainerInstance.addChildAt(child, index);
    }

    #addToLuminaryChildren(child: BaseAsset, index?: number) {
        if (child.#luminaryParent && child.#luminaryParent !== this) {
            const oldIndex = child.#luminaryParent.#luminaryChildren.indexOf(child);
            if (oldIndex !== -1) {
                child.#luminaryParent.#luminaryChildren.splice(oldIndex, 1);
            }
        } else if (child.#luminaryParent === this) {
            const existingIndex = this.#luminaryChildren.indexOf(child);
            if (existingIndex !== -1) {
                this.#luminaryChildren.splice(existingIndex, 1);
            }
        }
        if (typeof index === 'number') {
            const luminaryIndex = Math.min(index, this.#luminaryChildren.length);
            this.#luminaryChildren.splice(luminaryIndex, 0, child);
        } else {
            this.#luminaryChildren.push(child);
        }
        child.#luminaryParent = this;
    }

    get x(): number {
        return this.#pixiContainerInstance.x;
    }

    set x(value: number) {
        this.#pixiContainerInstance.x = value;
    }

    get y(): number {
        return this.#pixiContainerInstance.y;
    }

    set y(value: number) {
        this.#pixiContainerInstance.y = value;
    }

    get width(): number {
        return this.#pixiContainerInstance.width;
    }

    set width(value: number) {
        this.#pixiContainerInstance.width = value;
    }

    get height(): number {
        return this.#pixiContainerInstance.height;
    }

    set height(value: number) {
        this.#pixiContainerInstance.height = value;
    }

    get scale(): PIXI.ObservablePoint {
        return this.#pixiContainerInstance.scale;
    }

    set scale(value: PIXI.IPointData) {
        this.#pixiContainerInstance.scale.copyFrom(value);
    }

    get rotation(): number {
        return this.#pixiContainerInstance.rotation;
    }

    set rotation(value: number) {
        this.#pixiContainerInstance.rotation = value;
    }

    get alpha(): number {
        return this.#pixiContainerInstance.alpha;
    }

    set alpha(value: number) {
        this.#pixiContainerInstance.alpha = value;
    }

    get opacity(): number {
        return this.#pixiContainerInstance.alpha;
    }

    set opacity(value: number) {
        if (value === this.#pixiContainerInstance.alpha) {
            return;
        }
        assertNumberBetween(value, 0, 1);
        this.#pixiContainerInstance.alpha = value;
        this.requiresRedraw = true;
    }

    get visible(): boolean {
        return this.#pixiContainerInstance.visible;
    }

    set visible(value: boolean) {
        this.#pixiContainerInstance.visible = value;
    }

    get pivot(): PIXI.ObservablePoint {
        return this.#pixiContainerInstance.pivot;
    }

    set pivot(value: PIXI.IPointData) {
        this.#pixiContainerInstance.pivot.copyFrom(value);
    }

    get transform(): PIXI.Transform {
        return this.#pixiContainerInstance.transform;
    }

    get worldTransform(): PIXI.Matrix {
        return this.#pixiContainerInstance.worldTransform;
    }

    get worldAlpha(): number {
        return this.#pixiContainerInstance.worldAlpha;
    }

    get filters(): PIXI.Filter[] | null {
        return this.#pixiContainerInstance.filters;
    }

    set filters(value: PIXI.Filter[] | null) {
        this.#pixiContainerInstance.filters = value;
    }

    /**
     * The asset's mask.
     * This is the PIXI property extended for our update loop.
     * See PIXI documentation for more details. https://pixijs.download/dev/docs/PIXI.Container.html#mask
     */
    get mask(): PIXI.Container | null {
        return this.#pixiContainerInstance.mask as PIXI.Container | null; // We do not use PIXI.MaskData
    }

    set mask(mask: PIXI.Container | null) {
        if (mask === this.mask) {
            return;
        }
        this.#pixiContainerInstance.mask = mask;
        this.requiresRedraw = true;
    }

    get zIndex(): number {
        return this.#pixiContainerInstance.zIndex;
    }

    set zIndex(value: number) {
        this.#pixiContainerInstance.zIndex = value;
    }

    get destroyed(): boolean {
        return this.#pixiContainerInstance.destroyed;
    }

    get interactive(): boolean {
        return this.#pixiContainerInstance.interactive;
    }

    set interactive(value: boolean) {
        this.#pixiContainerInstance.interactive = value;
    }

    get cursor(): string {
        return this.#pixiContainerInstance.cursor;
    }

    set cursor(value: string) {
        this.#pixiContainerInstance.cursor = value;
    }

    getBounds(): PIXI.Rectangle {
        return this.#pixiContainerInstance.getBounds();
    }

    getLocalBounds(rect?: PIXI.Rectangle): PIXI.Rectangle {
        return this.#pixiContainerInstance.getLocalBounds(rect);
    }

    /**
     * Moves a child to a new Luminary index and translates that to the
     * correct PIXI index, since the PIXI children array may contain
     * internal display objects (sprites, graphics) that don't exist in
     * #luminaryChildren.
     */
    #setPixiChildIndex(child: BaseAsset, index: number): void {
        const currentIndex = this.#luminaryChildren.indexOf(child);
        if (currentIndex === -1) {
            return;
        }

        this.#luminaryChildren.splice(currentIndex, 1);
        const luminaryIndex = Math.min(index, this.#luminaryChildren.length);
        this.#luminaryChildren.splice(luminaryIndex, 0, child);

        let pixiIndex: number;
        if (luminaryIndex < this.#luminaryChildren.length - 1) {
            const nextSibling = this.#luminaryChildren[luminaryIndex + 1];
            pixiIndex = this.#pixiContainerInstance.children.indexOf(nextSibling._pixiContainer);
        } else {
            pixiIndex = this.#pixiContainerInstance.children.length - 1;
        }
        this.#pixiContainerInstance.setChildIndex(child._pixiContainer, pixiIndex);
    }

    getChildIndex(child: BaseAsset): number {
        return this.#luminaryChildren.indexOf(child);
    }

    updateTransform(): void {
        this.#pixiContainerInstance.updateTransform();
    }

    render(renderer: PIXI.Renderer): void {
        this.#pixiContainerInstance.render(renderer);
    }

    removeChild<T extends BaseAsset[]>(...children: T): T[0] {
        let result!: T[0];
        for (const child of children) {
            this.#pixiContainerInstance.removeChild(child._pixiContainer);
            const index = this.#luminaryChildren.indexOf(child);
            if (index !== -1) {
                this.#luminaryChildren.splice(index, 1);
            }
            child.#luminaryParent = null;
            result = child;
        }
        this.requiresRedraw = true;
        return result;
    }

    addChild<T extends BaseAsset[]>(...children: T): T[0] {
        let result!: T[0];
        for (const child of children) {
            this.#addToLuminaryChildren(child);
            this.#pixiContainerInstance.addChild(child._pixiContainer);
            result = child;
        }
        return result;
    }

    addChildAt<T extends BaseAsset>(child: T, index: number): T {
        // Calculate PIXI index BEFORE adding to Luminary children.
        // We need to find the right position in PIXI that maintains z-order relative
        // to internal PIXI children (graphics, helpers, etc.)
        let pixiIndex: number;
        if (index < this.#luminaryChildren.length) {
            // There's a BaseAsset at the insertion point - insert before it in PIXI
            const containerAtIndex = this.#luminaryChildren[index];
            pixiIndex = this.#pixiContainerInstance.children.indexOf(
                containerAtIndex._pixiContainer,
            );
        } else {
            // Adding at or beyond the end - append to PIXI
            pixiIndex = this.#pixiContainerInstance.children.length;
        }

        this.#addToLuminaryChildren(child, index);
        this.#pixiContainerInstance.addChildAt(child._pixiContainer, pixiIndex);

        return child;
    }

    // ─── BaseAsset-specific methods ─────────────────────────────────────────

    get #displaysHelpers() {
        return (
            this.displayBounds ||
            this.displayMaxBounds ||
            this.displayAnchor ||
            this.displayMaskHelpers
        );
    }

    /**
     * @internal
     */
    _getAllBaseAssetsWithDisplayedHelpers(): BaseAsset[] {
        const assets: BaseAsset[] = [];
        if (this.#displaysHelpers) {
            assets.push(this);
        }
        for (const child of this.children) {
            if (typeof child._getAllBaseAssetsWithDisplayedHelpers === 'function') {
                assets.push(...child._getAllBaseAssetsWithDisplayedHelpers());
            }
        }
        return assets;
    }

    #createHelper(
        type: 'bounds' | 'maxBounds' | 'anchor' = 'bounds',
        colorAttribute: 'boundsColor' | 'maxBoundsColor' | 'anchorColor' = 'boundsColor',
    ) {
        const settings = {
            asset: this,
            strokeColor: this[colorAttribute],
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
            name: `${this.name}_${type}_helper`,
        };
        let helper: AssetBoundsDisplay | AssetMaxBoundsDisplay | AssetAnchorDisplay;
        switch (type) {
            case 'bounds':
                helper = new AssetBoundsDisplay(settings);
                break;
            case 'maxBounds':
                helper = new AssetMaxBoundsDisplay(settings);
                break;
            case 'anchor':
                helper = new AssetAnchorDisplay(settings);
                break;
            default:
                throw Error(`Unknown helper of type ${type}`);
        }
        helper.requiresUpdate = true;
        return helper;
    }

    createHelpers() {
        const helpers = [];
        if (this.displayBounds) {
            helpers.push(this.#createHelper('bounds', 'boundsColor'));
        }
        if (this.displayMaxBounds) {
            helpers.push(this.#createHelper('maxBounds', 'maxBoundsColor'));
        }
        if (this.displayAnchor) {
            helpers.push(this.#createHelper('anchor', 'anchorColor'));
        }

        if (this.displayMaskHelpers) {
            if (this.mask && Config.isStencilMask(this.mask)) {
                helpers.push(...this.mask.createHelpers());
            }
        }

        return helpers;
    }

    /**
     * The global timeline this asset is positioned relative to.
     * @internal
     */
    protected get _globalTimeline(): TickerTimeline {
        return this.#timeline;
    }

    /**
     * Returns the absolute start time of the asset relative in the global timeline. This can help us determine when the
     * asset is playing or visible.
     */
    get absoluteStartTime(): number {
        const timeline = (this.luminaryParent as unknown as { _timeline: BaseTimeline })._timeline;
        return timeline.startTimeRelativeToAncestor(this.#timeline);
    }

    /**
     * Returns the absolute end time of the asset relative in the global timeline. This can help us determine when the
     * asset is playing or visible.
     */
    get absoluteEndTime(): number {
        const timeline = (this.luminaryParent as unknown as { _timeline: BaseTimeline })._timeline;
        return timeline.startTimeRelativeToAncestor(this.#timeline) + timeline.duration;
    }

    /**
     * Flag to indicate if asset loading is enabled. Note this cannot be changed
     * after instantiation.
     */
    get fetchResources(): FetchResources {
        return this.#fetchResources;
    }

    /** @internal */
    _propagatePlay(): void {
        // nothing by default
    }

    /** @internal */
    _propagatePause(): void {
        this.#sfxAudioAssets.forEach((audioAsset) => {
            audioAsset._propagatePause();
        });
    }

    get failedToLoad(): boolean {
        return [...this.#sfxAudioAssets.values()].some((a) => a.failedToLoad);
    }

    get isLoaded(): boolean {
        return [...this.#sfxAudioAssets.values()].every((a) => a.isLoaded);
    }

    get isDoneTryingToLoad(): boolean {
        const sfxDoneTrying = [...this.#sfxAudioAssets.values()].every((a) => a.isDoneTryingToLoad);
        return sfxDoneTrying && (this.isLoaded || this.failedToLoad);
    }

    get canPlay(): boolean {
        const sfxCanPlay = [...this.#sfxAudioAssets.values()].every((a) => a.canPlay);
        if (!this._shouldLoad) {
            return sfxCanPlay;
        }
        return sfxCanPlay && this.isDoneTryingToLoad;
    }

    get approximateMaxBounds() {
        return this.#pixiContainerInstance.getBounds(true);
    }

    isMorphMatch(_other: BaseAsset): boolean {
        return false;
    }

    syncMorphLinkedProperties(_other: BaseAsset): boolean {
        return false;
    }

    get morphExcludedProperties(): MorphableProperty[] {
        return [];
    }

    get boundsWithoutAnimation() {
        const topParent = this.#getTopLevelParent();
        if (!topParent || !topParent.#enableBoundsWithoutAnimationForSubtree) {
            throw new Error(`boundsWithoutAnimation is not enabled for ${this.name}`);
        }
        return this.#boundsWithoutAnimation;
    }

    /**
     * Returns the "luminary object" which directly contains this asset. This is either a
     * SceneGroup, Scene or Subscene.
     */
    get luminaryParent(): SceneGroup | Scene | Subscene | null {
        if (this.destroyed) {
            return null;
        }
        // I know this implementation looks confusing, but it's how luminary is organized right
        // now, cleaning this up would involve a larger luminary refactor, which we want to do at
        // some point.
        if (this.container.classType === 'Subscene') {
            // Subscenes are the container for their assets
            return this.container as Subscene;
        }
        if (Config.isScene(this.container.slide)) {
            // e.g. handles the slideScene container and the SceneWatermarkSettings container
            return this.container.slide;
        }
        if ((this.container as unknown as Scene) === this.#slide) {
            // Sometimes, usually in tests, we use the Scene as the container directly
            return this.container as unknown as Scene;
        }
        if (
            this.container.name === 'assetsWorkspace' &&
            this.container.container &&
            Config.isSceneGroup(this.container.container)
        ) {
            // The assets workspace on the scene group
            return this.container.container;
        }
        if (
            this.container.name === 'decorationsWorkspace' &&
            this.container.container.container &&
            Config.isSceneGroup(this.container.container.container)
        ) {
            // The decoration workspace where the decorations (example: background gradient) live in a CaptionsManager.
            return this.container.container.container as SceneGroup;
        }
        if (Config.isSceneGroup(this.container)) {
            return this.container;
        }
        const containersToForward = [
            'TransitionManager',
            'TransitionContainer',
            'AssetGroup',
            'CaptionsManager',
            'CompositingAsset',
        ];
        if (containersToForward.includes(this.container.classType)) {
            return this.container.luminaryParent;
        }
        throw new Error(`Could not find luminary parent for ${this.name}:${this.classType}`);
    }

    /**
     * Requests that this asset buffers some data at the given time.
     * Some examples are buffering video and caching textures.
     * @internal
     */
    async _bufferAtTime(_time: number, _bufferingMode: BufferingMode) {
        // by default do nothing
    }

    /**
     * Add a brand new mask of type maskType to the BaseAsset
     */
    setMask<T extends MaskType>(maskType: T, settings?: Record<string, unknown>): GetMaskType<T> {
        const mask = this.#createMask({
            ...(settings ?? {}),
            classType: maskType,
        });
        this.mask = mask;
        return mask as GetMaskType<T>;
    }

    /**
     * Returns true if the asset's position is controlled by it's parent asset
     */
    get isPositionControlledByParent(): boolean {
        const asset = this.getParent();
        if (!asset || !Config.isAsset(asset)) {
            return false;
        }
        return asset._controlsChildPosition;
    }

    /**
     * Returns the asset or a parent asset of the asset that can be freely positioned
     */
    getFreelyPositionedParent() {
        let freeAsset: BaseAsset = this;
        while (freeAsset.isPositionControlledByParent) {
            freeAsset = freeAsset.getParent() as BaseAsset;
        }
        return freeAsset;
    }

    /**
     *
     * It returns a a list of OpaqueAsset[] (after .save())
     */
    saveWithNestedAssets(): OpaqueAsset[] {
        return [this.save()];
    }

    resetTransformation() {
        this._pixiContainer.x = 0;
        this._pixiContainer.y = 0;
        this._pixiContainer.rotation = 0;
        this._pixiContainer.pivot.set(0, 0);
    }

    /**
     * Sets the transformation of the asset, while ensuring that the asset stays within the bounds of the scene.
     */
    setTransformationPositionWithinSceneBounds(x: number, y: number) {
        // The transformation represents the static positioning of an asset, but we want to clamp the position based
        // on its current bounds. So we determine the change in the transformation position and then ensure that the change
        // doesn't cause the current bounds to go beyond the scene bounds.
        const bounds = this.getBounds();
        const xShift = x - this.transformation.position.x;
        const yShift = y - this.transformation.position.y;
        const minXShift = 0 - bounds.x;
        const maxXShift = this.getReferenceResolution().width - bounds.width - bounds.x;
        const minYShift = 0 - bounds.y;
        const maxYShift = this.getReferenceResolution().height - bounds.height - bounds.y;
        x = this.transformation.position.x + Math.min(maxXShift, Math.max(minXShift, xShift));
        y = this.transformation.position.y + Math.min(maxYShift, Math.max(minYShift, yShift));

        this.transformation.position.x = x;
        this.transformation.position.y = y;
    }

    /**
     * Set the container position based on the transformation. For assets with DynamicStyling,
     * applyTransformation should ONLY be called after _prepareDynamicStylingForUpdate and before
     * _updateDynamicStyling, as we should not consider the dynamic styling (color/alpha graphics)
     * when calculating the position
     *
     * @param offsetX - additional offset to add to the x position
     * @param offsetY - additional offset to add to the y position
     * @param offsetRotation - additional offset to add to rotation
     */
    applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        if (this.isPositionControlledByParent) {
            return;
        }
        const bounds = this.getBounds();
        const { x, y } = getPositionFromTransformation(
            this.transformation.save() as TransformationType,
            bounds.width,
            bounds.height,
        );

        // apply
        this._pixiContainer.x = x + offsetX;
        this._pixiContainer.y = y + offsetY;
        this._pixiContainer.rotation = this.transformation.rotation + offsetRotation;
    }

    /**
     * A flag which determines if this asset should be editable by the end user.
     *
     * Editable in this context means making direct changes to the asset in any way – changing it's colour, text value,
     * sourceData, etc. If `isEditable = false` this asset shouldn't have controls visible to the user.
     *
     * We _do_ expect this asset to be indirectly changed or changed via bulk actions. For example, modifying the
     * template colour palette or swapping colours may modify this asset.
     */
    get isEditable(): boolean {
        return this.#isEditable;
    }

    set isEditable(value: boolean) {
        if (this.isEditable === value) {
            return;
        }
        this.#isEditable = value;
        this.requiresRedraw = true;
    }

    /**
     * A flag which determines if color related properties of this asset should be editable by the end user.
     * if `isColorEditable = false` this asset shouldn't have color related controls visible to the user.
     */
    get isColorEditable(): boolean {
        return this.#isColorEditable;
    }

    set isColorEditable(value: boolean) {
        if (this.isColorEditable === value) {
            return;
        }
        this.#isColorEditable = value;
        this.requiresRedraw = true;
    }

    get visibleWithUpdate(): boolean {
        return this.visible;
    }

    /**
     * Updates PIXI `visible` property and triggers luminary redraw. This should be used instead of Pixi's visible
     * property
     */
    set visibleWithUpdate(value: boolean) {
        if (this.visible === value) {
            return;
        }
        this._pixiContainer.visible = value;
        this.requiresRedraw = true;
    }

    /**
     * Returns the autoDraw value. If True, then this Object will immediately process any changes set by requiresRedraw.
     */
    get autoDraw(): boolean {
        return this.#autoDraw;
    }

    /**
     * Set autoDraw on this asset and all of its children top-down. When autoDraw is set to true all property changes
     * will automatically be processed.
     */
    set autoDraw(value: boolean) {
        if (this.autoDraw === value) {
            return;
        }
        const topParent = this.#getTopLevelParent();
        if (!topParent) {
            return;
        }
        topParent._setAutoDrawForAllChildren(value);
        if (value) {
            if (this.#drawEvaluationMode === AssetMode.BUILD) {
                this._checkAndFixPropertiesRecur(false);
            }
            this._buildAndDrawRecur();
        }
    }

    override get name() {
        return super.name;
    }

    override set name(name: string) {
        if (name === this.name) {
            return;
        }

        assertAssetName(name);
        const oldName = this.name;
        super.name = name;
        this.#updateTextPositionPresetNames(oldName, name);

        this.requiresRedraw = true;
    }

    #updateTextPositionPresetNames(oldName: string, newName: string) {
        // Assets not used in a traditional hierarchy, e.g. caption decorations, won't have access to a slide, nor
        // do they care about text position presets.
        if (!this.#slide) {
            return;
        }

        const presets = this.#slide.textPositionPresets as Record<string, { name: string }[]>;

        if (!presets) {
            return;
        }

        for (const presetName of Object.keys(presets)) {
            for (const preset of presets[presetName]) {
                if (preset.name === oldName) {
                    preset.name = newName;
                }
            }
        }
    }

    /**
     * Emits the assetCanPlay event indicating that the given asset can be played
     * back even though it might not be loaded yet. This is to ensure we don't
     * lock up workspace playback when a given asset takes longer then expected
     * to load.
     * @internal
     */
    _emitAssetCanPlay() {
        if (!this.sentCanPlay) {
            this.sentCanPlay = true;
            this.emit('assetCanPlay');
            if (this.luminaryParent) {
                this.luminaryParent.emit('assetCanPlay');
            }
        }
    }

    /**
     * Returns the top level parent. This will be the workScene for each Scene which contains all our PIXI renderable
     * components.
     * @protected
     */
    #getTopLevelParent(): BaseAsset | null {
        if (this._isFinalTopLevel) {
            return this;
        }
        // it's possible that our object isn't parent of any hierarchy just yet
        if (!this.parent) {
            return null;
        }
        return this.parent.#getTopLevelParent();
    }

    /**
     * Recursively sets autoDraw for itself and all children
     * @internal
     */
    _setAutoDrawForAllChildren(value: boolean) {
        this.#autoDraw = value;
        this.#morphEngine.setAutoUpdate(value);
        this.children.forEach((asset) => {
            asset._setAutoDrawForAllChildren(value);
        });
    }

    /**
     * Adds one or more children to the container.
     *
     * Multiple items can be added like so: `myContainer.addChild(thingOne, thingTwo, thingThree)`
     *
     * @returns  The first child that was added.
     */
    insertAsset(asset: BaseAsset) {
        this.insertAssetAt(asset, this.children.length);
        return asset;
    }

    /**
     * Adds a child to the container at a specified index. If the index is out of bounds an error will be thrown
     */
    insertAssetAt(asset: BaseAsset, index: number) {
        // unparent child if necessary
        if (Config.isAsset(asset)) {
            const parent = asset.getParent();
            if (parent) {
                parent.removeChild(asset);
            }
        }

        // parent child
        const targetIndex = this._clampChildIndex(index);
        if (targetIndex !== -1) {
            this.addChildAt(asset, targetIndex);

            asset.autoDraw = this.autoDraw;
            asset.requiresRedraw = true;

            // Register asset with Luminary for O(1) lookups
            this.#root?._registerAsset?.(asset as Asset);
        }
    }

    moveChild(object: BaseAsset, index: number): boolean {
        const targetIndex = this._clampChildIndex(index);
        if (targetIndex !== -1) {
            this.#setPixiChildIndex(object, targetIndex);
            this.requiresRedraw = true;
            return true;
        }
        return false;
    }

    /** @internal */
    _clampChildIndex(targetIndex: number): number {
        if (targetIndex < 0) {
            return 0;
        }
        if (targetIndex > this.children.length) {
            return this.children.length;
        }
        return targetIndex;
    }

    /**
     * Check if this asset intersects with any target assets.
     * @internal
     */
    _intersectsAny(targetAssets: BaseAsset[]): boolean {
        const sourceBounds = this.getBounds();
        const sourceLeft = Math.floor(sourceBounds.x);
        const sourceRight = Math.ceil(sourceBounds.x + sourceBounds.width);
        const sourceTop = Math.floor(sourceBounds.y);
        const sourceBottom = Math.ceil(sourceBounds.y + sourceBounds.height);
        for (const targetAsset of targetAssets) {
            const targetBounds = targetAsset.getBounds();
            if (
                targetBounds.contains(sourceLeft, sourceTop) ||
                targetBounds.contains(sourceLeft, sourceBottom) ||
                targetBounds.contains(sourceRight, sourceTop) ||
                targetBounds.contains(sourceRight, sourceBottom)
            ) {
                return true;
            }
        }
        return false;
    }

    /**
     * Determine if this asset or any of its children require to be redrawn
     * @internal
     */
    get _recursiveRequiresRedraw(): boolean {
        if (this.requiresRedraw) {
            return true;
        }
        return this.children.some((asset) => {
            if (typeof asset.requiresRedraw !== 'boolean') {
                return false;
            }
            if (typeof asset._recursiveRequiresRedraw !== 'function') {
                return asset.requiresRedraw;
            }
            return asset._recursiveRequiresRedraw;
        });
    }

    /**
     * Returns whether or not the object has changes that need to be drawn.
     */
    get requiresRedraw(): boolean {
        return this.#requiresRedraw;
    }

    /**
     * Set whether or not the given changes requires us to redraw the object. If for example we change the color or size
     * of the object then we need to pass on a value of True. This will then redraw the object with the new color or size.
     * @param value
     */
    set requiresRedraw(value: boolean) {
        assertBoolean(value);

        this.#requiresRedraw = value;

        if (value) {
            if (this.#drawEvaluationMode === AssetMode.BUILD) {
                this._checkAndFixPropertiesRecur(false);
            }
            this._buildAndDrawRecur();
        }
    }

    /** @internal */
    _childRequestingRedraw(child: BaseAsset) {
        this._childrenRequestingRedraw.push(child);
        this.requiresRedraw = true;
        this._childrenRequestingRedraw = [];
    }

    /**
     * Indicates if the asset should be loaded as a result of fetchResources setting.
     * @internal
     */
    get _shouldLoad(): boolean {
        if (typeof this.fetchResources !== 'boolean') {
            throw new Error(
                `${this.classType} needs to override _shouldLoad to handle fetchResources as an object`,
            );
        }
        return this.fetchResources;
    }

    /**
     * Allows an asset to specify conditions which will prevent a build and draw from happening.
     * @internal
     */
    get _skipBuildAndDraw(): boolean {
        return false;
    }

    /**
     * The building logic stub. To be overridden by the child class.
     *
     * The _build method is called whenever the asset is created, or when the user changes a property and we need to
     * create a new scene image.
     *
     * The _build method is not called by properties set by animation curves.
     */
    protected _build() {
        // should be defined within the child class if we want to build something
        this._draw();
    }

    /**
     * The draw logic stub. To be overridden by the child class.
     *
     * The _draw method is called on every ${@link _build} call (whenever the user changes a property), and also
     * when an animation changes a property. So if a property is going to be animated, it's value should be read
     * in this method, then it should be copied over to the pixi objects that will be rendered.
     */
    protected _draw() {
        // should be defined within the child class if we want to draw something
    }

    updateForRender() {
        if (this.mask instanceof StencilMask) {
            this.mask.updateForRender();
        }
    }

    /**
     * Runs for the current state of the BaseAsset, correcting or updating any
     * properties as necessary. It's important that this method does not throw errors, but
     * rather fixes the properties in-line.
     * @internal
     */
    _checkAndFixProperties() {
        const inSubscene = !!this.subscene || this.classType === 'Subscene';
        if (
            inSubscene &&
            this.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_NEVER &&
            this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE).length === 0
        ) {
            this.animateOutBehaviour = Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING;
        } else if (
            !inSubscene &&
            this.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING
        ) {
            this.animateOutBehaviour = Config.ANIMATE_OUT_BEHAVIOUR_NEVER;
        }
    }

    /** @internal */
    _checkAndFixPropertiesRecur(traverseChildren = true) {
        // if we're drawing (aka the #processing flag) we don't need to recompute properties and definitely
        // don't want to start drawing again – return early!
        if (this.#processing) {
            return;
        }

        // no longer build if object is destroyed
        if (this.destroyed) {
            return;
        }

        // Make sure the object is part of a hierarchy (scene)
        // If it hasn't yet been attached to a parent then we don't need to draw and/or check and fix properties yet.
        if (!this.parent && (!this._isFinalTopLevel || this._updateParentOnRedraw)) {
            return;
        }

        // activate the "processing" (aka building/drawing) flag so we don't reevaluate when updating properties in
        // the following `_checkAndFixProperties()` call or when calling `_fixBuildDraw()` on children
        this.#processing = true;

        if (traverseChildren) {
            this.children.forEach((asset) => {
                asset._checkAndFixPropertiesRecur();
            });
        }

        this._checkAndFixProperties();

        if (this.parent) {
            this.parent._checkAndFixPropertiesRecur(false);
        }

        this.#processing = false;
    }

    /** @internal */
    _buildAndDrawRecur() {
        if (!this.autoDraw) {
            return;
        }

        // if we're drawing (aka the #processing flag) we don't need to recompute properties and definitely
        // don't want to start drawing again – return early!
        if (this.#processing) {
            return;
        }

        // no longer build if object is destroyed
        if (this.destroyed) {
            return;
        }

        // Make sure the object is part of a hierarchy (scene)
        // If it hasn't yet been attached to a parent then we don't need to draw and/or check and fix properties yet.
        if (!this.parent && (!this._isFinalTopLevel || this._updateParentOnRedraw)) {
            return;
        }

        // activate the "processing" (aka building/drawing) flag so we don't reevaluate when updating properties in
        // the following `_checkAndFixProperties()` call or when calling `_fixBuildDraw()` on children
        this.#processing = true;

        this.children.forEach((asset) => {
            asset._buildAndDrawRecur();
        });

        if (!this.requiresRedraw) {
            this.#processing = false;
            return;
        }

        try {
            // process local changes
            if (!this._skipBuildAndDraw) {
                if (this.#drawEvaluationMode === AssetMode.BUILD) {
                    this._build();

                    if (this._isFinalTopLevel && this.#enableBoundsWithoutAnimationForSubtree) {
                        // once the top of the hierarchy has been built, we can cache the bounds
                        this._cacheBoundsAfterBuildRecursively();
                    }
                } else {
                    this._draw();
                }
            }
        } finally {
            // all local changes have now been processed
            this.requiresRedraw = false;
            this.#processing = false;
        }

        if (this.#drawEvaluationMode === AssetMode.BUILD) {
            if (this._updateLuminaryParentOnRedraw) {
                if (!this.luminaryParent) {
                    console.error(
                        '_updateLuminaryParentOnRedraw is set to true, but we have no Luminary parent.',
                    );
                }
                this.luminaryParent!.requiresUpdate = true;
            } else if (this._updateParentOnRedraw) {
                if (!this.parent) {
                    console.warn(
                        `Unable to redraw the parent for ${this.name} since parent is ${this.parent}.`,
                    );
                    return;
                }
                // ask the parent asset to redraw
                this.parent._childRequestingRedraw(this);
            } else {
                // All the dependent assets have been drawn, inform the scene we're finished updating
                this._updateEvent();
            }
        }
    }

    /**
     * This method gets called once everything has been updated.
     * @internal
     */
    _updateEvent() {
        this._updateEventSource.emit('childUpdated', this.name);
    }

    /**
     * This recursively caches the bounds of all children. This is intended to be used immediately
     * after a build and will cache the latest bounds for each asset in the whole subtree.
     *
     * The first call should be made at the top of a hierarchy with skipUpdate set to false. That
     * way the bounds for the whole subtree are updated once. Then each recursive call will not
     * again re-calculate the bounds.
     * @internal
     */
    _cacheBoundsAfterBuildRecursively(opts = { skipUpdate: false }) {
        this.#pixiContainerInstance.getBounds(opts.skipUpdate, this.#boundsWithoutAnimation);
        this.children.forEach((asset) => {
            if (asset._isFinalTopLevel) {
                // There are some nested trees. E.g. SceneGroup & Scene. We don't need to drill
                // down into a nested hierarchy from the outer one because the nested one will
                // also do its own update if necessary.
                return;
            }
            asset._cacheBoundsAfterBuildRecursively({ skipUpdate: true });
        });
    }

    /**
     * Animate or progress to a certain value. This will update any driven properties and set any requiresRedraw to True
     * if changed
     * @internal
     */
    // eslint-disable-next-line unused-imports/no-unused-vars
    _seek(progress: number, seekMode: SeekMode) {
        if (!this.animationManager) {
            return;
        }
        // limit our computations by only evaluating values that will trigger a change
        if (progress >= 0 && progress <= 1) {
            this.animationManager.percentage = progress;
        }
        if (this.mask instanceof PixiContainer) {
            const maskBounds = this.mask.getBounds();
            // Ensure the mask isn't offscreen. Otherwise, we won't see its effect, and we'll have to hide the masked
            // object ourselves.
            if (maskBounds.x + maskBounds.width < 0 || maskBounds.y + maskBounds.height < 0) {
                this._pixiContainer.visible = false;
            } else {
                this._pixiContainer.visible = true;
            }
        }
    }

    /**
     * Calls _seek for itself and all of its children
     * @internal
     */
    _recursiveSeek(progress: number, seekMode: SeekMode) {
        this.children.forEach((asset) => {
            asset._recursiveSeek(progress, seekMode);
        });
        // update all driven properties
        this._seek(progress, seekMode);
    }

    /**
     * Applies intro animations to the asset. This method should be called by derived classes after `_applyAnimation()`
     * in their `_build()` implementation.
     * @internal
     */
    _applyIntroAnimations() {
        const hasIntroAnimation = this.animationManager.hasAnchors(
            Config.START_ANIMATION_CURVE_ANCHOR,
        );
        if (!hasIntroAnimation) {
            return;
        }
        const isOnFirstSubscene = this.subscene && this.subscene.index === 0;
        const isNotOnSubsceneLevel = !this.subscene;
        if (!isOnFirstSubscene && !isNotOnSubsceneLevel) {
            return;
        }

        const timeOffset = this.introAnimationOffset / this.container.duration;
        const morphedProperties = this.morphAnimation.map((animation) => animation.property);
        this.animationManager.moveAllGroupsExceptOnesDrivingProperties(
            timeOffset,
            0,
            Config.START_ANIMATION_CURVE_ANCHOR,
            morphedProperties,
        );
    }

    /**
     * Applies outro animations to the asset. This method should be called by derived classes after `_applyAnimation()`
     * in their `_build()` implementation.
     * @internal
     */
    _applyOutroAnimations() {
        const hasOutroAnimation = this.animationManager.hasAnchors(
            Config.END_ANIMATION_CURVE_ANCHOR,
        );
        if (!hasOutroAnimation) {
            return;
        }

        const timeOffset = this.outroAnimationOffset / this.container.duration;
        const morphedProperties = this.morphAnimation.map((animation) => animation.property);
        this.animationManager.moveAllGroupsExceptOnesDrivingProperties(
            -timeOffset,
            0,
            Config.END_ANIMATION_CURVE_ANCHOR,
            morphedProperties,
        );
    }

    /** @internal */
    get _durationAnimatedIn(): number {
        if (
            this.animationManager.hasAnchors(Config.END_ANIMATION_CURVE_ANCHOR) &&
            this.animationManager.hasAnchors(Config.START_ANIMATION_CURVE_ANCHOR)
        ) {
            return this.animationManager.outroEnd - this.animationManager.introStart;
        }
        if (this.animationManager.hasAnchors(Config.END_ANIMATION_CURVE_ANCHOR)) {
            return this.animationManager.outroEnd;
        }
        if (this.animationManager.hasAnchors(Config.START_ANIMATION_CURVE_ANCHOR)) {
            const endingTime = Math.max(this.container.duration, this.animationManager.introEnd);
            return endingTime - this.animationManager.introStart;
        }
        return this.container.duration;
    }
    /**
     * Animate or evaluate the object and its children to the given progress
     */
    seek(progress: number, seekMode: SeekMode) {
        this._batchRedraw(() => {
            // set draw evaluation mode to EVAL mode so we only evaluate our existing hierarchy properties
            // and don't build any new elements.
            this.#setDrawEvaluationMode(AssetMode.EVAL);
            this._recursiveSeek(progress, seekMode);
        });

        // reset draw evaluation mode back to BUILD mode
        this.#setDrawEvaluationMode(AssetMode.BUILD);
    }

    /**
     * Set the internal drawing mode to either ASSET_EVAL_MODE or ASSET_BUILD_MODE.
     */
    #setDrawEvaluationMode(value: AssetMode) {
        assertInEnum(value, AssetMode);
        this.#drawEvaluationMode = value;
        this.children.forEach((asset) => {
            asset.#setDrawEvaluationMode(value);
        });
    }

    get introAnimationOffset() {
        return this.#introAnimationOffset;
    }

    set introAnimationOffset(value) {
        if (this.introAnimationOffset === value) {
            return;
        }
        this.#introAnimationOffset = value;
        this.requiresRedraw = true;
    }

    get outroAnimationOffset() {
        return this.#outroAnimationOffset;
    }

    set outroAnimationOffset(value) {
        if (this.outroAnimationOffset === value) {
            return;
        }
        this.#outroAnimationOffset = value;
        this.requiresRedraw = true;
    }

    /**
     * The introAnimationOffset is used to calculate the actual time that an asset will start its intro animation.
     * For example, if the asset is in a group, the asset's intro animation could be staggered by the other
     * assets in the group.
     *
     * appliedIntroAnimationOffset is the actual time that the asset will start its intro animation.
     */
    get appliedIntroAnimationOffset() {
        if (!this.animationManager || !this.container) {
            return 0;
        }
        const { parentOffset, parentHasAnimation } = (() => {
            const assetGroupParent = this.getParentByType(Config.GROUP_ASSET_TYPE);
            if (!assetGroupParent || !assetGroupParent.hasObjectAnimation) {
                return { parentOffset: 0, parentHasAnimation: false };
            }
            return {
                parentOffset: assetGroupParent.introAnimationOffset,
                parentHasAnimation: true,
            };
        })();

        const xPoints = this.animationManager.groups.flatMap((group) => {
            return group.animCurves
                .flatMap((curve) => {
                    const points = curve.getEndPoints(Config.START_ANIMATION_CURVE_ANCHOR);
                    for (let i = 0; i < points.length; i++) {
                        const currentPoint = points[i];
                        const nextPoint = points[i + 1];
                        if (nextPoint && nextPoint.y !== currentPoint.y) {
                            return currentPoint.x;
                        }
                    }
                    return null;
                })
                .filter((x) => x !== null);
        });

        const ownIntroAnimationOffset =
            xPoints.length > 0 ? Math.min(...xPoints) * this.container.duration : 0;
        return parentHasAnimation
            ? Math.max(parentOffset, ownIntroAnimationOffset)
            : ownIntroAnimationOffset;
    }

    /**
     * Similar to {@link appliedIntroAnimationOffset}, but for the outro animation.
     */
    get appliedOutroAnimationOffset() {
        if (!this.animationManager || !this.container) {
            return 0;
        }
        const { parentOffset, parentHasAnimation } = (() => {
            const assetGroupParent = this.getParentByType(Config.GROUP_ASSET_TYPE);
            if (!assetGroupParent || !assetGroupParent.hasObjectAnimation) {
                return { parentOffset: 0, parentHasAnimation: false };
            }
            return {
                parentOffset: assetGroupParent.outroAnimationOffset,
                parentHasAnimation: true,
            };
        })();

        const xPoints = this.animationManager.groups.flatMap((group) => {
            return group.animCurves
                .flatMap((curve) => {
                    const points = curve.getEndPoints(Config.END_ANIMATION_CURVE_ANCHOR);
                    for (let i = points.length - 1; i >= 0; i--) {
                        const currentPoint = points[i];
                        const prevPoint = points[i - 1];
                        if (prevPoint && prevPoint.y !== currentPoint.y) {
                            return currentPoint.x;
                        }
                    }
                    return null;
                })
                .filter((x) => x !== null);
        });

        const ownOutroAnimationOffset =
            xPoints.length > 0 ? (1 - Math.max(...xPoints)) * this.container.duration : 0;
        return parentHasAnimation
            ? Math.max(parentOffset, ownOutroAnimationOffset)
            : ownOutroAnimationOffset;
    }

    /**
     * The scaling multiplier to take a reference dimension to the output dimension.
     * e.g. myOutputCoord =  myReferenceCoord * this.referenceToOutputMultiplier
     */
    get referenceToOutputMultiplier() {
        return this.getOutputResolution().width / this.getReferenceResolution().width;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'animationConfig',
            'animateOutBehaviour',
            'transformation',
            'filters',
            'isEditable',
            'isColorEditable',
            'introAnimationOffset',
            'outroAnimationOffset',
            'opacity',
            'sfx',
        ]);
    }

    override get readonlyProperties() {
        return super.readonlyProperties.concat(['parentId']);
    }

    /**
     * Custom mask load method used in load
     * NOTE: we don't add "mask" as a property to serialize here, but we do in classes that inherit BaseAsset
     * @internal
     */
    _maskCustomLoad(properties: { classType: MaskType } | undefined) {
        // properties is undefined when we saved out a mask property which wasn't set
        if (!properties) {
            return;
        }
        if (!(this.mask === null || this.mask === undefined)) {
            return;
        }
        this.mask = this.#createMask(properties);
    }

    /**
     * Custom mask save method used in save
     * NOTE: we don't add "mask" as a property to serialize here, but we do in classes that inherit BaseAsset
     * @internal
     */
    _maskCustomSave(): object | null {
        if (!this.mask) {
            return null;
        }
        if (this.mask instanceof StencilMask) {
            return this.mask.save();
        } else {
            return null;
        }
    }

    /**
     * Custom visible load method used in load
     * NOTE: we need this because Pixi defines the visible property so we created our own to propagate updates
     * @internal
     */
    _visibleCustomLoad(value: boolean) {
        this.visibleWithUpdate = value;
    }

    #createMask<T extends MaskType>(properties: { classType: T }): GetMaskType<T> {
        let mask: CircleMask | PolygonMask;
        const settings = {
            name: this.name + '_mask',
            updateTarget: this,
            getReferenceResolution: this.getReferenceResolution,
        };
        // create the necessary mask
        switch (properties.classType) {
            case Config.CIRCLE_MASK_TYPE:
                mask = new CircleMask(settings);
                break;
            case Config.POLYGON_MASK_TYPE:
                mask = new PolygonMask(settings);
                break;
            default:
                throw Error(`Unknown mask type ${properties.classType}`);
        }
        mask.load(properties);
        return mask as GetMaskType<T>;
    }

    /**
     * Returns the container where filters are applied for the asset.
     * @internal
     */
    protected get _pixiFilterContainer(): PIXI.DisplayObject {
        return this._pixiContainer;
    }

    /** @internal */
    _filtersCustomSave() {
        if (!this.#filters) {
            return null;
        }
        return this.#filters.map((filter) => filter.save());
    }

    /** @internal */
    _filtersCustomLoad(data: { name: string; classType: string }[] | undefined) {
        if (!data) return;

        data.forEach((config: { name: string; classType: string }) => {
            // Only need to create the filter if it doesn't yet exist
            let filter = this.getFilterByName(config.name);
            if (!filter) {
                filter = this.addFilter(
                    config.classType as Parameters<typeof this.addFilter>[0],
                    config.name,
                );
            }
            filter.load(config);
        });
    }

    /**
     * Add filter and triggers an update. Use this method instead of directly adding a filter to the filters array.
     * If providing a name, it needs to be unique among the filters already added to this asset.
     */
    addFilter(classType: 'BlurFilter', name?: string): BlurFilter;
    addFilter(classType: 'AdjustmentFilter', name?: string): AdjustmentFilter;
    addFilter(classType: 'ColorChannelMapFilter', name?: string): ColorChannelMapFilter;
    addFilter(classType: 'GreyscaleFilter', name?: string): GreyscaleFilter;
    addFilter(classType: 'DuoToneFilter', name?: string): DuoToneFilter;
    addFilter(classType: 'HslAdjustmentFilter', name?: string): HslAdjustmentFilter;
    addFilter(classType: 'OldFilmFilter', name?: string): OldFilmFilter;
    addFilter(classType: 'ColorGradingFilter', name?: string): ColorGradingFilter;
    addFilter(
        classType:
            | 'BlurFilter'
            | 'AdjustmentFilter'
            | 'ColorChannelMapFilter'
            | 'GreyscaleFilter'
            | 'DuoToneFilter'
            | 'HslAdjustmentFilter'
            | 'OldFilmFilter'
            | 'ColorGradingFilter',
        name?: string,
    ): Filter {
        let filter: Filter;
        const settings = {
            name,
            parent: this,
            fetchResources: this.#fetchResources,
        };

        if (!settings.name) {
            settings.name = `${classType}_${(this.#filters?.filter((f) => f.classType === classType).length || 0) + 1}`;
        }

        if (this.#filters && this.#filters.some((f) => f.name === settings.name)) {
            throw Error(`Name of filter should be unique`);
        }

        assertInArray(classType, Config.ALL_FILTERS);

        switch (classType) {
            case Config.BLUR_FILTER:
                filter = new BlurFilter(settings);
                break;
            case Config.ADJUSTMENT_FILTER:
                filter = new AdjustmentFilter(settings);
                break;
            case Config.COLOR_CHANNEL_MAP_FILTER:
                filter = new ColorChannelMapFilter(settings);
                break;
            case Config.GREYSCALE_FILTER:
                filter = new GreyscaleFilter(settings);
                break;
            case Config.DUO_TONE_FILTER:
                filter = new DuoToneFilter(settings);
                break;
            case Config.HSL_ADJUSTMENT_FILTER:
                filter = new HslAdjustmentFilter(settings);
                break;
            case Config.OLD_FILM_FILTER:
                filter = new OldFilmFilter(settings);
                break;
            case Config.COLOR_GRADING_FILTER:
                filter = new ColorGradingFilter(settings);
                break;
            default:
                throw Error(`Unable to create unknown filter ${classType}`);
        }

        if (!this._pixiFilterContainer.filters) {
            this._pixiFilterContainer.filters = [];
        }

        if (this.root.renderer) {
            filter.resolution = this.root.renderer.resolution;
        }

        this.#filters.push(filter);
        this._pixiFilterContainer.filters.push(filter.pixiFilter);
        this.requiresRedraw = true;
        return filter;
    }

    /**
     * Retrieves the list of luminary filters. Once we separate the pixi hierarchy from the luminary hierarchy, we
     * can remove this method and just use a getter for filters. (Currently PIXI defines the filters getter)
     */
    getFilters(): Filter[] {
        return this.#filters;
    }

    /**
     * Get filter by name
     */
    getFilterByName(name: string): Filter | undefined {
        return this.#filters?.find((filter) => filter.name === name);
    }

    /**
     * Remove filter by name
     */
    removeFilter(name: string) {
        const index = this.#filters.findIndex((filter) => filter.name === name);
        if (index === -1) {
            return;
        }
        const filter = this.#filters[index];
        const pixiIndex = this._pixiFilterContainer.filters?.indexOf(filter.pixiFilter) ?? -1;

        this.#filters.splice(index, 1);
        if (pixiIndex !== -1) {
            this._pixiFilterContainer.filters!.splice(pixiIndex, 1);
        }
        filter.destroy();
        this.requiresRedraw = true;
    }

    protected removeFilterIfNeutral(name: string) {
        const filter = this.getFilterByName(name);
        if (!filter) {
            return;
        }
        if (filter.isNeutral()) {
            this.removeFilter(name);
        }
    }

    /**
     * Bulk load properties with autoDraw disabled
     */
    // TODO(strict-ts): Change to Record<string, unknown> when serializableMixin is updated
    override load(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataObj: Record<string, any>,
        settings: LoadSettings = { skipTemplateAttributes: false },
    ) {
        this._batchRedraw(() => {
            super.load(dataObj, settings);
        });
        return this;
    }

    loadAsSticker(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataObj: Record<string, any>,
        settings: LoadSettings = { skipTemplateAttributes: false },
    ) {
        const stickerDataObj = {
            ...dataObj,
            createdFrom: AssetCreatedFrom.STICKER,
            name: uniqueName(
                'sticker',
                this._requireSlide
                    .getAssets()
                    .filter((asset) => (asset as BaseAsset) !== this)
                    .filter(Config.isStickerAsset)
                    .map((asset) => asset.name),
            ),
        };
        this.load(stickerDataObj, settings);
        const asset = this as unknown as Asset;
        if (Config.isTextAsset(asset) || Config.isFancyCaptionAsset(asset)) {
            asset.textDirection = this._requireSlide.textDirection;
        }
        return this;
    }

    /**
     * Load all editable properties with autoDraw disabled.
     */
    // TODO(strict-ts): Change to Record<string, unknown> when serializableMixin is updated
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    override loadEditable(dataObj: Record<string, any>) {
        this._batchRedraw(() => {
            super.loadEditable(dataObj);
        });
        return this;
    }

    hasSuffix(value: string): boolean {
        const suffix = (() => {
            const match = this.name.match(ASSET_NAME_REGEX);
            if (!match) {
                return '';
            }
            return match[3] ?? '';
        })();

        return suffix.includes(value);
    }

    /**
     * Get all of the child attributes that are linked to a template with their associated reference value. This
     * includes dynamic styling and filters. We return an object where the attribute is the key and the reference
     * value is the value.
     */
    buildTemplateReferenceMap(): AssetTemplateReferenceMap {
        const referenceMap: Record<string, string> = {};

        // get asset properties
        const templateAttributes = getAllTemplateAttributes(
            this,
            ValueMapMode.GET_REFERENCE_VALUES,
        );
        templateAttributes.forEach((attr) => {
            const val = getTemplateValueAttribute(this, attr) as string;
            referenceMap[attr] = stripTemplateReferencePrefix(val);
        });
        // get filter properties
        if (Array.isArray(this.#filters)) {
            this.#filters.forEach((filter, index) => {
                const attrKeyPrefix = `filters.${index}`;
                const templateAttributes = getAllTemplateAttributes(
                    filter,
                    ValueMapMode.GET_REFERENCE_VALUES,
                );
                templateAttributes.forEach((attr) => {
                    const attrKey = `${attrKeyPrefix}.${attr}`;
                    const val = getTemplateValueAttribute(filter, attr) as string;
                    referenceMap[attrKey] = stripTemplateReferencePrefix(val);
                });
            });
        }

        return referenceMap;
    }

    /** @internal */
    _hasTemplateReferenceValues(): boolean {
        return objectHasTemplateValues(this);
    }

    /**
     * This method can be overriden to define which part of an asset is a style property.
     */
    get stylePropertiesToCopy(): string[] {
        return [];
    }

    copyStyleProperties(): CopyPasteData {
        return getStylePropertiesToCopy(this);
    }

    /**
     * Validate template reference map and load settings in luminary.
     */
    pasteStyleProperties(settings: CopyPasteData) {
        const templateIndexErrorsList: string[] = [];

        findTemplateIndexErrors(
            this.template,
            '',
            settings._templateReferenceMap,
            templateIndexErrorsList,
        );

        if (templateIndexErrorsList.length > 0) {
            throw new Error(templateIndexErrorsList.join(' '));
        }

        this.load(settings);
    }

    /** @internal */
    get _animationAnchorsToRemove() {
        return this.#animationAnchorsToRemove;
    }

    set _animationAnchorsToRemove(value: RemoveAnchoredPoints) {
        if (this._animationAnchorsToRemove === value) {
            return;
        }
        assertInArray(value, Config.ANIMATION_CURVE_ANCHORS);
        if (
            this.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_ALWAYS &&
            value === Config.END_ANIMATION_CURVE_ANCHOR
        ) {
            throw new Error(
                `${this.name}'s animate out behaviour does not allow removing the end anchor`,
            );
        }
        this.#animationAnchorsToRemove = value;
        this.requiresRedraw = true;
    }

    /**
     * Defines the allowed behaviour of all exit animations for this class.
     * If ALWAYS is set, then the asset will ensure no end anchor points are removed.
     * If WHEN_PRECEDING is set, then the asset will allow end anchor points to be removed
     * by calling removeOutAnimationIfAllowed
     */
    get animateOutBehaviour(): string {
        return this.#animateOutBehaviour;
    }

    set animateOutBehaviour(value: string) {
        if (this.animateOutBehaviour === value) {
            return;
        }
        assertInArray(value, Config.ANIMATE_OUT_BEHAVIOURS);
        this.#animateOutBehaviour = value;
        switch (this.#animateOutBehaviour) {
            case Config.ANIMATE_OUT_BEHAVIOUR_ALWAYS:
                this.#animationAnchorsToRemove = Config.NONE_ANIMATION_CURVE_ANCHOR;
                break;
            case Config.ANIMATE_OUT_BEHAVIOUR_NEVER:
            case Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING:
                this.#animationAnchorsToRemove = Config.END_ANIMATION_CURVE_ANCHOR;
                break;
        }
        this.requiresRedraw = true;
    }

    /**
     * Removes all internal references and listeners as well as removes children from the display list.
     * Do not use a BaseAsset after calling `destroy`.
     */
    destroy(options?: DestroyOptions) {
        // Detach from parent's internal collections. We inline the removal rather
        // than calling the potentially-overridden removeChild (e.g.,
        // AssetGroup.removeChild only accepts logical child assets, not internal
        // structural children like _moveContainer).
        if (this.#luminaryParent) {
            this.#luminaryParent.#pixiContainerInstance.removeChild(this.#pixiContainerInstance);
            const index = this.#luminaryParent.#luminaryChildren.indexOf(this);
            if (index !== -1) {
                this.#luminaryParent.#luminaryChildren.splice(index, 1);
            }
            this.#luminaryParent = null;
        }

        this.#clearSfxAudio();

        // Unregister from Luminary asset index before destroying
        this.#root?._unregisterAsset?.(this.id);

        this.#destroyFilters();

        if (this.#animationManager) {
            this.#animationManager.destroy();
            this.#animationManager = null as unknown as AnimationCurveManager;
        }

        // Recursively destroy Luminary children if requested
        const shouldDestroyChildren =
            options === true || (typeof options === 'object' && options?.children);
        if (shouldDestroyChildren) {
            for (const child of [...this.#luminaryChildren]) {
                child.destroy(options);
            }
        } else {
            for (const child of this.#luminaryChildren) {
                child.#luminaryParent = null;
            }
        }
        this.#luminaryChildren = [];

        // Destroy the internal PIXI container
        this.#pixiContainerInstance.destroy(options);

        this.container = null;
        this.#root = null;
        this.#slide = null;
    }

    #destroyFilters() {
        this.#filters.forEach((filter) => {
            filter.destroy();
        });
        this.#filters = [];
        this._pixiFilterContainer.filters = null;
    }

    /** @internal */
    _batchRedraw(work: () => void) {
        const initAutoDraw = this.autoDraw;
        this.autoDraw = false;
        try {
            work();
        } finally {
            this.autoDraw = initAutoDraw;
        }
    }

    get morphAnimation(): DynamicAnimationConfiguration[] {
        return this.#morphEngine.animations;
    }

    get hasMorphAnimation(): boolean {
        return this.#morphEngine.hasAnimations;
    }

    get hasMorphLeadInAnimation(): boolean {
        return MorphManager.isMorphTarget(this.id);
    }

    addMorphAnimation(): DynamicAnimationConfiguration {
        return this.#morphEngine.addAnimation();
    }

    clearMorphAnimations(): void {
        this.#morphEngine.clearAnimations();
    }

    /** @internal */
    _updateMorphAnimation(): void {
        this.#morphEngine.update();
    }

    /**
     * Returns true if the asset has any audio (including SFX).
     * Returns null if the audio state is unknown (e.g. sourceData not yet backfilled).
     */
    get hasAudio(): boolean | null {
        return this.#sfx.length > 0;
    }

    /**
     * Returns the list of sound effects attached to this asset.
     */
    get sfx(): readonly AssetSfxItem[] {
        return this.#sfx;
    }

    /**
     * Add a sound effect to this asset.
     * @param settings - Settings for the SFX including sourceData, event, and offset
     */
    addSfx(settings: AddAssetSfxSettings): AssetSfxItem {
        const sfx: AssetSfxItem = {
            id: uuid(),
            sourceData: settings.sourceData,
            event: settings.event,
            offset: settings.offset ?? 0,
        };
        this.#sfx = [...this.#sfx, sfx];

        this.#createSfxAudioAsset(sfx);
        this.requiresRedraw = true;

        return sfx;
    }

    /**
     * Remove a sound effect from this asset by its ID.
     * @param id - The ID of the SFX to remove
     */
    removeSfx(id: string): void {
        const initialLength = this.#sfx.length;
        this.#sfx = this.#sfx.filter((s) => s.id !== id);
        if (this.#sfx.length !== initialLength) {
            const audioAsset = this.#sfxAudioAssets.get(id);
            if (audioAsset) {
                audioAsset._propagatePause();
                audioAsset.destroy();
                this.#sfxAudioAssets.delete(id);
            }
            this.requiresRedraw = true;
        }
    }

    /**
     * Get a sound effect by its ID.
     * @param id - The ID of the SFX to retrieve
     * @returns The AssetSfxItem if found, undefined otherwise
     */
    getSfxById(id: string): AssetSfxItem | undefined {
        return this.#sfx.find((s) => s.id === id);
    }

    /**
     * Create an AudioAsset for an SFX item.
     */
    #createSfxAudioAsset(sfx: AssetSfxItem): void {
        const audioAsset = new AudioAsset({
            fetchResources: this.fetchResources,
            name: `sfx_${sfx.id}`,
            parent: this,
            autoUpdate: true,
            id: sfx.id,
            getAudioContext: () => this.root.audioContext,
            getIsExternallyMuted: () => this.root.muted,
        });

        audioAsset.sourceData = sfx.sourceData;
        audioAsset.loop = false;

        this.#sfxAudioAssets.set(sfx.id, audioAsset);
    }

    /**
     * Get the AudioAsset for a sound effect by its ID.
     * @param id - The ID of the SFX
     * @returns The AudioAsset if found, undefined otherwise
     * @internal
     */
    _getSfxAudioAsset(id: string): AudioAsset | undefined {
        return this.#sfxAudioAssets.get(id);
    }

    /**
     * Calculate the global playback time for a given SFX based on its event trigger.
     * @param sfx - The SFX item to calculate playback time for
     * @returns The global playback time in milliseconds
     * @internal
     */
    _getSfxPlaybackTime(sfx: AssetSfxItem): number {
        const offset = sfx.offset ?? 0;

        switch (sfx.event) {
            case AssetSfxEvent.ENTER:
                // Play at the point when the asset becomes visible (start of intro animation)
                return this.absoluteStartTime + this.appliedIntroAnimationOffset + offset;
            default:
                ErrorReporter.setContext('sfx', { event: sfx.event });
                throw new Error('Invalid asset SFX event');
        }
    }

    /**
     * Clean up all SFX audio assets.
     * Pauses and destroys all AudioAssets and clears both the map and the list.
     */
    #clearSfxAudio(): void {
        this.#sfxAudioAssets.forEach((audioAsset) => {
            audioAsset._propagatePause();
            audioAsset.destroy();
        });
        this.#sfxAudioAssets.clear();
        this.#sfx = [];
    }

    /**
     * Custom load method for sfx deserialization.
     * @internal
     */
    _sfxCustomLoad(data: AssetSfxItem[] | null): void {
        if (!data || !Array.isArray(data)) {
            this.#sfx = [];
            return;
        }
        this.#sfx = data;

        this.#sfx.forEach((sfx) => {
            this.#createSfxAudioAsset(sfx);
        });
    }
}
